import React, { useState } from 'react';
import { Button, IconButton } from '@lumx/react';
import { mdiChevronDown, mdiChevronUp, mdiHelpCircleOutline, mdiLock, mdiMagnify } from '@mdi/js';
import MdiIcon from '@mdi/react';
import { Icon } from './shared/icons';
import { useWizard, WizardPage } from '../context/WizardContext';
import { useOrgData } from '../context/OrgDataContext';

const chevron = (down = true) => (
  <MdiIcon path={down ? mdiChevronDown : mdiChevronUp} size={0.65} color="rgba(0,0,0,0.38)" />
);

const Sidebar: React.FC = () => {
  const { currentPage, navigateTo, sitesAssigned, sitesTotal, resetProgress } = useWizard();
  const { resetPrototype } = useOrgData();
  const [govExpanded, setGovExpanded] = useState(true);

  const handleReset = () => {
    if (!window.confirm('Reset prototype? This clears all mappings, removes custom org units, and resets migration progress.')) return;
    resetPrototype();
    resetProgress();
    navigateTo('migration-progress');
  };

  const navItem = (label: string, page: WizardPage, active: boolean, locked = false) => (
    <button
      className={`sidebar-item${active ? ' active' : ''}${locked ? ' sidebar-item--locked' : ''}`}
      onClick={() => !locked && navigateTo(page)}
      style={{ paddingLeft: 32, fontSize: 13 }}
      title={locked ? 'Complete the previous step first' : undefined}
    >
      {locked && (
        <span className="sidebar-icon" style={{ opacity: 0.4 }}>
          <MdiIcon path={mdiLock} size={0.65} />
        </span>
      )}
      {label}
    </button>
  );

  /* Non-functional decorative item matching production */
  const decor = (label: string, icon: React.ReactNode, hasChevron = false) => (
    <button className="sidebar-item">
      <span className="sidebar-icon">{icon}</span>
      <span style={{ flex: 1 }}>{label}</span>
      {hasChevron && chevron()}
    </button>
  );

  return (
    <nav className="sidebar">
      <div className="sidebar-top">
        <div className="sidebar-brand">
          <div className="sidebar-brand-logo">
            <svg viewBox="0 0 28 28" fill="none" width="28" height="28">
              <rect width="28" height="28" rx="6" fill="#1a1a2e"/>
              <path d="M14 6L20 20H8L14 6Z" fill="#ffffff" fillOpacity="0.9"/>
            </svg>
          </div>
          <span className="sidebar-brand-name">Acme Corp</span>
        </div>
        <div className="sidebar-top-actions">
          <div className="sidebar-avatar" title="Jennifer Kahn">JK</div>
          <IconButton label="Help" icon={mdiHelpCircleOutline} emphasis="low" size="m" />
        </div>
      </div>

      <div className="sidebar-search">
        <div className="sidebar-search__field">
          <span className="sidebar-search__placeholder">Search</span>
          <span className="sidebar-search__kbd">⌘K</span>
        </div>
        <button className="sidebar-search__btn" aria-label="Search">
          <MdiIcon path={mdiMagnify} size={0.75} />
        </button>
      </div>

      <div className="sidebar-nav">

        {/* ── Content / Site ─────────────────────────────── */}
        <div className="sidebar-group">
          <div className="sidebar-section-label">Content</div>
          <div className="sidebar-items">
            {decor('Navigation',          Icon.nav.navigation)}
            {decor('Content types',       Icon.nav.contentTypes)}
            {decor('Media',               Icon.nav.media)}
            {decor('Metadata',            Icon.nav.metadata)}
            {decor('Roles',               Icon.nav.roles)}
            {decor('Site administrators', Icon.nav.siteAdmins)}
            {decor('Site groups',         Icon.nav.siteGroups)}
            {decor('Style',               Icon.nav.style)}
          </div>
        </div>

        {/* ── Mobile ─────────────────────────────────────── */}
        <div className="sidebar-group">
          <div className="sidebar-section-label">Mobile</div>
          <div className="sidebar-items">
            {decor('General settings',  Icon.nav.generalSettings)}
            {decor('Customization',     Icon.nav.customization)}
            {decor('Login',             Icon.nav.login,            true)}
            {decor('Navigation',        Icon.nav.mobileNavigation)}
            {decor('App configuration', Icon.nav.appConfig)}
          </div>
        </div>

        {/* ── Play ───────────────────────────────────────── */}
        <div className="sidebar-group">
          <div className="sidebar-section-label">Play</div>
          <div className="sidebar-items">
            {decor('Video usage', Icon.nav.videoUsage)}
          </div>
        </div>

        {/* ── Platform ───────────────────────────────────── */}
        <div className="sidebar-group">
          <div className="sidebar-section-label">Platform</div>
          <div className="sidebar-items">

            {/* Org units & Locations — functional */}
            <button
              className={`sidebar-item${currentPage === 'org-units' ? ' active' : ''}`}
              onClick={() => navigateTo('org-units')}
            >
              <span className="sidebar-icon">{Icon.nav.orgUnits}</span>Org units &amp; Locations
            </button>

            {/* Governance migration — functional, expandable */}
            <button
              className="sidebar-item sidebar-item--parent"
              onClick={() => setGovExpanded(e => !e)}
            >
              <span className="sidebar-icon">{Icon.nav.orgAssign}</span>
              <span style={{ flex: 1, textAlign: 'left' }}>Governance migration</span>
              {govExpanded ? chevron(false) : chevron(true)}
            </button>

            {govExpanded && (
              <div className="sidebar-sub-items">
                {navItem('Migration progress',   'migration-progress',   currentPage === 'migration-progress')}
                {navItem('Org mapping',         'site-mapping',         currentPage === 'site-mapping')}
                {navItem('Test deployment', 'test-deployment', currentPage === 'test-deployment')}
                {navItem('Deployment',           'deployment',           currentPage === 'deployment' || currentPage === 'post-deployment')}
              </div>
            )}

            {/* Production Platform items */}
            {decor('Platform settings',    Icon.nav.platformSettings,   true)}
            {decor('Domains management',   Icon.nav.domainsManagement,  true)}
            {decor('Features Management',  Icon.nav.featuresManagement)}
            {decor('Extensions',           Icon.nav.extensions,         true)}
            {decor('Global administrators',Icon.nav.globalAdmins)}
            {decor('Provider settings',    Icon.nav.providerSettings,   true)}
            {decor('User management',      Icon.nav.userManagement)}

            {/* Platform groups — functional */}
            <button
              className={`sidebar-item${currentPage === 'platform-groups' ? ' active' : ''}`}
              onClick={() => navigateTo('platform-groups')}
            >
              <span className="sidebar-icon">{Icon.nav.platformGroups}</span>Platform groups
            </button>

            {decor('Sites', Icon.nav.sites, true)}
          </div>
        </div>

      </div>

      <div className="sidebar-reset-footer">
        <Button emphasis="medium" size="m" onClick={handleReset} fullWidth>
          Reset prototype
        </Button>
      </div>
    </nav>
  );
};

export default Sidebar;
